import { useContext, useEffect, useRef, useState } from 'react'
import type { GetServerSidePropsContext } from 'next'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import styled from 'styled-components'
import {
  AutoScrollSlider,
  Breadcrumbs,
  Container,
  Meta,
  PageTransition,
  Search,
  Section,
  Song,
} from 'components'
import { CollectionSlider, SongTile } from 'components/CollectionSlider'
import { SongJsonLd } from 'components/Meta'
import { Typography } from 'components-library/typography'
import { AnalyticsContext } from 'contexts/analyticsContext'
import { AnimatePresence } from 'framer-motion'
import {
  getArtistInfoFromPath,
  getGenreInfoFromPath,
  Song as SongBasic,
  SongDetailed,
  SongVersion,
} from 'gtsongsapi/types'
import {
  useGoogleAds,
  useManageFeatureFlags,
  useSongSettings,
  useTrackSongViewed,
} from 'hooks'
import { getSong, getSongRecommendations } from 'utils/fetchFromApi'
import { getCapoPosition } from 'utils/song/getCapoPosition'
import { getCanonicalSongUrl } from 'utils/urls'

const AdBanner = dynamic(() => import('components/AdBanner'), { ssr: false })

export type SongSettings = {
  version: SongVersion
  transposeLevel: number
  capoStatus: boolean
}

// Defer loading offscreen components
// https://nextjs.org/docs/pages/building-your-application/optimizing/lazy-loading#nextdynamic
const NumberStats = dynamic(() => import('../../components/NumberStats'), {
  ssr: false,
})

const Slider = dynamic(() => import('../../components/Slider'), {
  ssr: false,
})

const Footer = dynamic(() => import('../../components/Footer'), {
  ssr: false,
})

//#region Styles
const Styled = {
  Root: styled.div`
    width: 100%;
    display: grid;
    gap: 4rem;
    padding-top: 16px;
  `,
  SongContainer: styled(Container)`
    position: relative;
    display: grid;
    gap: 16px;
    @media (min-width: 1024px) {
      gap: 24px;
    }
  `,
  BackgroundImage: styled.div`
    position: absolute;
    opacity: 0.8;
    left: 0;
    top: 0;
    width: 100%;
    height: 40rem;
    filter: blur(2rem);
    object-fit: cover;
    z-index: -1;
  `,
  SongRecommendationsContainer: styled(Container)`
    padding: 0;
  `,
  AdContainer: styled(Container)`
    position: relative;
    width: 100%;
    max-width: 100vw;
    overflow: hidden;
    min-height: 200px;
    text-align: center;
  `,
}
//#endregion

export default function Page({
  serverSong,
  songRecommendations,
}: {
  serverSong: SongDetailed
  songRecommendations: SongBasic[]
}) {
  const pathname = usePathname()
  const { analytics } = useContext(AnalyticsContext)
  const adsVisible = useGoogleAds()
  const [clientSong, setClientSong] = useState(serverSong)
  const { _id: songId, title, artist_name: artistName } = serverSong

  const [pageHydrated, setPageHydrated] = useState(false)
  const [songWithSettingsLoading, setSongWithSettingsLoading] = useState(true)

  const { trackSongViewed, isLoading } = useTrackSongViewed()
  const preSongIdRef = useRef(songId)

  //#region Song settings
  const getCapoForVersion = (version: SongVersion) => {
    return getCapoPosition(clientSong, version)
  }
  const {
    transposeLevel,
    songVersion,
    setTransposeLevel,
    setSongVersion,
    capoStatus,
    toggleCapo,
  } = useSongSettings(songId, getCapoForVersion)
  const capo = getCapoForVersion(songVersion)
  //#endregion

  //#region Feature flags
  const { isFeatureEnabled } = useManageFeatureFlags([
    'web_gtsongs_song_rating',
  ])
  const isRatingEnabled = isFeatureEnabled('web_gtsongs_song_rating')
  //#endregion

  //#region Breadcrumbs
  const genreInfoFromPath = pathname && getGenreInfoFromPath(pathname)
  const artistInfoFromPath = pathname && getArtistInfoFromPath(pathname)
  const breadcrumbs = [
    ...(genreInfoFromPath
      ? [genreInfoFromPath]
      : artistInfoFromPath
        ? [artistInfoFromPath]
        : []),
    { name: clientSong?.title || title, path: pathname },
  ]
  //#endregion

  //#region Meta data
  const metaTitle = `${title} chords by ${artistName}`
  const metaDescription = `Learn how to play ${title} chords by ${artistName} with GuitarTuna by Yousician`
  //#endregion

  useEffect(() => {
    if (!isLoading && songId) {
      trackSongViewed(songId)
    }
  }, [])

  useEffect(() => {
    setClientSong(serverSong)
    setPageHydrated(true)
    setSongWithSettingsLoading(false)
  }, [serverSong])

  useEffect(() => {
    if (!pageHydrated || isLoading) return

    if (preSongIdRef.current !== songId) {
      preSongIdRef.current = songId
      return
    }

    if (songWithSettingsLoading) return
    setSongWithSettingsLoading(true)

    const abortController = new AbortController()

    const handleUpdate = async () => {
      try {
        const res = await fetch('/chords/api/update_song_settings/', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            version: songVersion,
            transpose_level: transposeLevel,
            capo_status: capoStatus,
            songId,
          }),
          signal: abortController.signal, // Pass the abort signal
        })
        if (!res.ok) throw new Error('Update failed')

        const newSongData = await res.json()
        setClientSong(newSongData)

        // update analytics
        await analytics.trackSongSettingsChanged({
          transpose_level: transposeLevel,
          song_version: songVersion,
          capo: capoStatus,
        })
      } catch (error: any) {
        console.error('Error happens when updating song settings:', error)
        setClientSong(serverSong)

        if (error instanceof TypeError && error.message === 'Failed to fetch') {
          if (navigator.onLine) {
            // Likely blocked by ad blocker/CORS
            console.error('Request blocked by client:', error)
            alert('Request blocked! Disable ad blockers or check permissions.')
          } else {
            // User is offline
            alert('You are offline.')
          }
        } else if (error.name === 'AbortError') {
          // Request was canceled by AbortController
          console.log('Request aborted')
        }
      }
    }

    handleUpdate()
    setSongWithSettingsLoading(false)
    return () => abortController.abort()
  }, [
    songVersion,
    capoStatus,
    transposeLevel,
    songId,
    analytics,
    songWithSettingsLoading,
    isLoading,
    pageHydrated,
    serverSong,
  ])

  if (!pageHydrated) return null

  return (
    <>
      <Meta
        title={metaTitle}
        description={metaDescription}
        url={getCanonicalSongUrl(clientSong, true)}
        image_url={clientSong.image_urls?.tile ?? ''}
      />
      <SongJsonLd
        song={clientSong}
        pathname={getCanonicalSongUrl(clientSong, true)}
      />
      <AnimatePresence>
        <PageTransition>
          <Styled.Root>
            <Styled.SongContainer>
              <Styled.BackgroundImage>
                <Image
                  src={
                    clientSong.image_urls?.list ??
                    '/chords/images/missing_cover.jpg'
                  }
                  alt={title}
                  fill={true}
                  priority={true}
                  style={{ objectFit: 'cover' }}
                />
              </Styled.BackgroundImage>
              <Breadcrumbs steps={breadcrumbs} />
              <Search />
              <Song
                key={clientSong._id} // Force remount when song changes
                song={clientSong}
                genreInfoFromPath={genreInfoFromPath}
                transposeLevel={transposeLevel}
                setTransposeLevel={setTransposeLevel}
                loading={songWithSettingsLoading}
                songVersion={songVersion}
                setSongVersion={setSongVersion}
                isRatingEnabled={isRatingEnabled}
                capo={capo}
                capoStatus={capoStatus}
                toggleCapo={toggleCapo}
              />
              <AutoScrollSlider />
            </Styled.SongContainer>
            {!!songRecommendations.length && (
              <Section>
                <Styled.SongRecommendationsContainer data-testid="song-recommendations">
                  <CollectionSlider<SongBasic>
                    name="More songs to play"
                    slides={songRecommendations}
                    tile={SongTile}
                    seeAll={{ path: '/', type: 'tile' }}
                  />
                </Styled.SongRecommendationsContainer>
              </Section>
            )}
            {adsVisible && (
              <Section>
                <Styled.AdContainer data-testid="song-ad-banner">
                  <AdBanner slot="7936828909" />
                </Styled.AdContainer>
              </Section>
            )}
            <Section background="#2E2F34" paddingTop="56" paddingBottom="56">
              <Container>
                <Typography
                  variant="h3"
                  color="white"
                  align="center"
                  spaceBottom={48}
                >
                  Join millions playing with <span>GuitarTuna</span>
                </Typography>
                <NumberStats />
              </Container>
            </Section>
            <Section>
              <Slider />
            </Section>
            <Footer hideButtons={null} />
          </Styled.Root>
        </PageTransition>
      </AnimatePresence>
    </>
  )
}

// This function runs at request time, and pre-renders the page on each request with the returned props
export async function getServerSideProps(context: GetServerSidePropsContext) {
  // This value is considered fresh for 7 days (s-maxage=604800).
  // If a request is repeated within the next 7 days, the previously
  // cached value will still be fresh. If the request is repeated before 1 month,
  // the cached value will be stale but still render (stale-while-revalidate=2678400).

  // In the background, a revalidation request will be made to populate the cache
  // with a fresh value. If you refresh the page, you will see the new value.
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=604800, stale-while-revalidate=2678400',
  )
  const {
    query: { id },
  } = context

  try {
    const song = await getSong(`songid_${id}`)
    const songRecommendations = await getSongRecommendations(`songid_${id}`)

    return {
      props: {
        serverSong: song,
        songRecommendations,
      },
    }
  } catch (error) {
    return {
      notFound: true,
    }
  }
}
